// src/components/Contacts.js
import React, { useState, useEffect } from 'react';
import {
  Box, 
  Input, 
  Button, 
  List, 
  ListItem, 
  Text, 
  IconButton, 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalFooter, 
  ModalBody, 
  ModalCloseButton, 
  useDisclosure, 
  Checkbox, 
  Flex, 
  Spacer, 
  VStack, 
  HStack,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Badge,
  useToast,
  Progress,
  useColorModeValue
} from '@chakra-ui/react';
import { db, auth } from '../firebase';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { DeleteIcon, EditIcon, PhoneIcon, AddIcon, SearchIcon } from '@chakra-ui/icons';

const formatPhoneNumber = (phone) => {
  let cleanPhone = phone.replace(/[^\d+]/g, '');
  if (cleanPhone.startsWith('+972')) {
    return cleanPhone;
  }
  if (cleanPhone.startsWith('972')) {
    return '+' + cleanPhone;
  }
  if (cleanPhone.startsWith('0')) {
    return '+972' + cleanPhone.slice(1);
  }
  return '+972' + cleanPhone;
};

const displayPhoneNumber = (phone) => {
  if (phone.startsWith('+972')) {
    return '0' + phone.slice(4);
  }
  return phone;
};

const Contacts = ({ onContactSelect }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [contacts, setContacts] = useState([]);
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [search, setSearch] = useState('');
  const [editContact, setEditContact] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userDocument, setUserDocument] = useState(null);
  const toast = useToast();

  // צבעים עבור Dark/Light mode
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    const fetchContacts = async () => {
      setLoading(true);
      const user = auth.currentUser;
      if (user) {
        try {
          // טען נתוני משתמש
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setUserDocument(userDoc.data());
          }
          
          // טען אנשי קשר
          const contactsCollection = collection(db, `users/${user.uid}/contacts`);
          const contactSnapshot = await getDocs(contactsCollection);
          const contactList = contactSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setContacts(contactList);
          // console.log(`Loaded ${contactList.length} contacts`);
        } catch (error) {
          console.error('Error fetching contacts:', error);
          toast({
            title: 'שגיאה',
            description: 'נכשל בטעינת אנשי הקשר',
            status: 'error',
            duration: 3000,
            isClosable: true
          });
        }
      }
      setLoading(false);
    };
    fetchContacts();
  }, [toast]);

  useEffect(() => {
    onContactSelect(selectedContacts);
  }, [selectedContacts, onContactSelect]);

  const addContact = async () => {
    const user = auth.currentUser;
    if (user && newContactName && newContactPhone) {
      try {
        const formattedPhone = formatPhoneNumber(newContactPhone);
        const docRef = await addDoc(collection(db, `users/${user.uid}/contacts`), { 
          name: newContactName, 
          phone: formattedPhone,
          created_at: new Date().toISOString()
        });
        
        const newContact = { 
          id: docRef.id, 
          name: newContactName, 
          phone: formattedPhone,
          created_at: new Date().toISOString()
        };
        
        setContacts([...contacts, newContact]);
        setNewContactName('');
        setNewContactPhone('');
        onClose();
        
        toast({
          title: 'הוסף בהצלחה',
          description: `איש הקשר ${newContactName} נוסף למערכת`,
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } catch (error) {
        console.error('Error adding contact:', error);
        toast({
          title: 'שגיאה',
          description: 'נכשל בהוספת איש הקשר',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
      }
    }
  };

  const updateContact = async (id, name, phone) => {
    const user = auth.currentUser;
    if (user) {
      try {
        const formattedPhone = formatPhoneNumber(phone);
        const contactDoc = doc(db, `users/${user.uid}/contacts`, id);
        await updateDoc(contactDoc, { 
          name, 
          phone: formattedPhone,
          updated_at: new Date().toISOString()
        });
        
        setContacts(contacts.map(contact => (
          contact.id === id 
            ? { ...contact, name, phone: formattedPhone, updated_at: new Date().toISOString() } 
            : contact
        )));
        setEditContact(null);
        
        toast({
          title: 'עודכן בהצלחה',
          description: `פרטי ${name} עודכנו`,
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } catch (error) {
        console.error('Error updating contact:', error);
        toast({
          title: 'שגיאה',
          description: 'נכשל בעדכון איש הקשר',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
      }
    }
  };

  const deleteContact = async (id) => {
    const user = auth.currentUser;
    if (user) {
      try {
        await deleteDoc(doc(db, `users/${user.uid}/contacts`, id));
        setContacts(contacts.filter(contact => contact.id !== id));
        setSelectedContacts(selectedContacts.filter(contactId => contactId !== id));
        
        toast({
          title: 'נמחק בהצלחה',
          description: 'איש הקשר נמחק מהמערכת',
          status: 'info',
          duration: 3000,
          isClosable: true
        });
      } catch (error) {
        console.error('Error deleting contact:', error);
        toast({
          title: 'שגיאה',
          description: 'נכשל במחיקת איש הקשר',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
      }
    }
  };

  const toggleSelectContact = (id) => {
    const maxContacts = userDocument?.features?.unlimited_calls ? 999 : 10;
    
    if (selectedContacts.includes(id)) {
      setSelectedContacts(selectedContacts.filter(contactId => contactId !== id));
    } else {
      if (selectedContacts.length >= maxContacts) {
        toast({
          title: 'הגבלת בחירה',
          description: `ניתן לבחור עד ${maxContacts} אנשי קשר בכל פעם`,
          status: 'warning',
          duration: 3000,
          isClosable: true
        });
        return;
      }
      setSelectedContacts([...selectedContacts, id]);
    }
  };

  const selectAllContacts = () => {
    const maxContacts = userDocument?.features?.unlimited_calls ? 999 : 10;
    const filteredContacts = contacts.filter(contact => 
      contact.name.toLowerCase().includes(search.toLowerCase()) || 
      contact.phone.includes(search)
    );
    
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      const contactsToSelect = filteredContacts.slice(0, maxContacts);
      if (contactsToSelect.length < filteredContacts.length) {
        toast({
          title: 'הגבלת בחירה',
          description: `נבחרו רק ${maxContacts} אנשי קשר הראשונים (מתוך ${filteredContacts.length})`,
          status: 'info',
          duration: 3000,
          isClosable: true
        });
      }
      setSelectedContacts(contactsToSelect.map(contact => contact.id));
    }
  };

  const filteredContacts = contacts.filter(contact => 
    contact.name.toLowerCase().includes(search.toLowerCase()) || 
    contact.phone.includes(search)
  );

  if (loading) {
    return (
      <Card variant="outline" borderColor={borderColor}>
        <CardBody>
          <VStack spacing={4}>
            <Progress size="lg" isIndeterminate colorScheme="teal" width="100%" />
            <Text>טוען אנשי קשר...</Text>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card variant="outline" borderColor={borderColor}>
      <CardHeader>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
          <Heading size="md">אנשי קשר</Heading>
          <HStack>
            <Badge colorScheme="teal" fontSize="sm" borderRadius="full" px={3}>
              {contacts.length} אנשי קשר
            </Badge>
            {!userDocument?.features?.unlimited_calls && (
              <Badge colorScheme="orange" fontSize="sm" borderRadius="full" px={3}>
                מוגבל ל-10 שיחות
              </Badge>
            )}
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {/* חיפוש */}
          <HStack>
            <Input
              placeholder="חפש אנשי קשר..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              rightElement={<SearchIcon color="gray.300" />}
            />
          </HStack>

          {/* כפתורי פעולה */}
          <HStack spacing={3}>
            <Button 
              leftIcon={<AddIcon />} 
              onClick={onOpen}
              colorScheme="teal"
              size="md"
            >
              הוסף איש קשר
            </Button>
            <Button 
              onClick={selectAllContacts}
              variant="outline"
              size="md"
            >
              {selectedContacts.length === filteredContacts.length ? 'בטל בחירת הכל' : 'בחר הכל'}
            </Button>
            {selectedContacts.length > 0 && (
              <Badge colorScheme="blue" fontSize="sm" borderRadius="full" px={3}>
                נבחרו: {selectedContacts.length}
                {!userDocument?.features?.unlimited_calls && ' / 10'}
              </Badge>
            )}
          </HStack>

          {/* רשימת אנשי קשר */}
          <List spacing={3}>
            {filteredContacts.length > 0 ? (
              filteredContacts.map(contact => (
                <ListItem 
                  key={contact.id} 
                  borderWidth={1} 
                  borderRadius="lg" 
                  p={4}
                  bg={selectedContacts.includes(contact.id) ? 'teal.50' : bgColor}
                  borderColor={selectedContacts.includes(contact.id) ? 'teal.200' : borderColor}
                  _hover={{ bg: hoverBg }}
                  transition="all 0.2s"
                >
                  <Flex alignItems="center">
                    <Checkbox
                      isChecked={selectedContacts.includes(contact.id)}
                      onChange={() => toggleSelectContact(contact.id)}
                      mr={3}
                      colorScheme="teal"
                      size="lg"
                    />
                    
                    {editContact === contact.id ? (
                      <Flex flex="1" gap={3}>
                        <Input
                          value={contact.name}
                          onChange={(e) =>
                            setContacts(
                              contacts.map(c => (c.id === contact.id ? { ...c, name: e.target.value } : c))
                            )
                          }
                          placeholder="שם"
                        />
                        <Input
                          value={displayPhoneNumber(contact.phone)}
                          onChange={(e) =>
                            setContacts(
                              contacts.map(c => (c.id === contact.id ? { ...c, phone: formatPhoneNumber(e.target.value) } : c))
                            )
                          }
                          placeholder="טלפון"
                        />
                        <Button 
                          onClick={() => updateContact(contact.id, contact.name, contact.phone)}
                          colorScheme="green"
                          size="sm"
                        >
                          שמור
                        </Button>
                        <Button 
                          onClick={() => setEditContact(null)}
                          variant="outline"
                          size="sm"
                        >
                          בטל
                        </Button>
                      </Flex>
                    ) : (
                      <Flex flex="1" alignItems="center">
                        <VStack align="start" spacing={1} flex="1">
                          <Text fontWeight="bold" fontSize="lg">{contact.name}</Text>
                          <HStack>
                            <PhoneIcon color="gray.500" />
                            <Text color="gray.600" fontSize="md">
                              {displayPhoneNumber(contact.phone)}
                            </Text>
                          </HStack>
                        </VStack>
                        
                        <HStack>
                          <IconButton
                            icon={<EditIcon />}
                            onClick={() => setEditContact(contact.id)}
                            aria-label="ערוך איש קשר"
                            size="sm"
                            variant="outline"
                            colorScheme="blue"
                          />
                          <IconButton
                            icon={<DeleteIcon />}
                            onClick={() => deleteContact(contact.id)}
                            aria-label="מחק איש קשר"
                            size="sm"
                            variant="outline"
                            colorScheme="red"
                          />
                        </HStack>
                      </Flex>
                    )}
                  </Flex>
                </ListItem>
              ))
            ) : (
              <Box textAlign="center" py={8}>
                <Text color="gray.500" fontSize="lg">
                  {search ? 'לא נמצאו אנשי קשר התואמים לחיפוש' : 'אין אנשי קשר במערכת'}
                </Text>
                {!search && (
                  <Button mt={4} onClick={onOpen} colorScheme="teal">
                    הוסף איש קשר ראשון
                  </Button>
                )}
              </Box>
            )}
          </List>
        </VStack>
      </CardBody>

      {/* מודל הוספת איש קשר */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>הוסף איש קשר חדש</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Input
                placeholder="שם איש קשר"
                value={newContactName}
                onChange={(e) => setNewContactName(e.target.value)}
              />
              <Input
                placeholder="מספר טלפון (לדוגמה: 0501234567)"
                value={newContactPhone}
                onChange={(e) => setNewContactPhone(e.target.value)}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="teal" 
              onClick={addContact}
              isDisabled={!newContactName || !newContactPhone}
              mr={3}
            >
              הוסף
            </Button>
            <Button variant="outline" onClick={onClose}>
              בטל
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default Contacts;